import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-store';

/**
 * `<recently-viewed-products>` — renders the user's recently viewed products
 * on the current page.
 *
 * The product list is stored per-browser in localStorage (see
 * `@theme/recently-viewed-store`). On first viewport intersection, this
 * element reads the stored list, filters out the current product's handle,
 * and fetches the section's rendered HTML via Shopify's Section Rendering
 * API under the `/search` endpoint using `handle:X OR handle:Y ...` as the
 * query. That lets the section's Liquid access the matching products via
 * `search.results` and emit `_product-card` blocks — including the custom
 * `pv-card-price-entries` block — without duplicating any Liquid logic here.
 *
 * Because Shopify's search doesn't preserve insertion order, the script
 * re-sorts the returned `.resource-list__item` nodes by the localStorage
 * sequence before inserting them into the DOM.
 *
 * Empty / error / zero-results behavior: the whole section is hidden via
 * the `hidden` attribute. This is an enhancement, not a critical feature.
 */
class RecentlyViewedProducts extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#hydrate();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #hydrated = false;

  connectedCallback() {
    super.connectedCallback();

    // If the section was rendered with search.performed=true (Section
    // Rendering API response), the cards are already in place — skip JS.
    if (this.dataset.searchPerformed === 'true') {
      this.#hydrated = true;
      return;
    }

    // Server renders the section with `hidden` by default (see section
    // liquid) so the skeleton never flashes on first visits / empty state.
    // We only unhide if the user has recently-viewed items other than the
    // current product. Hidden elements have 0 size → IntersectionObserver
    // won't fire, so we MUST unhide before observing.
    if (this.#getFilteredEntries().length === 0) {
      return;
    }

    this.hidden = false;
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Read localStorage, strip the current product's handle, respect the
   * per-section `max_products` setting.
   *
   * @returns {Array<{ id: string, handle: string }>}
   */
  #getFilteredEntries() {
    const currentHandle = this.dataset.currentHandle ?? '';
    const max = parseInt(this.dataset.maxProducts ?? '4', 10) || 4;
    return RecentlyViewed.getProducts()
      .filter((entry) => entry.handle && entry.handle !== currentHandle)
      .slice(0, max);
  }

  async #hydrate() {
    if (this.#hydrated) return;
    this.#hydrated = true;

    const entries = this.#getFilteredEntries();
    if (entries.length === 0) {
      this.hidden = true;
      return;
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      this.hidden = true;
      return;
    }

    const query = entries.map((e) => `handle:${e.handle}`).join(' OR ');
    const url = new URL(Theme?.routes?.search_url ?? '/search', location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', sectionId);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    let html;
    try {
      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });
      if (!response.ok) {
        this.hidden = true;
        return;
      }
      html = await response.text();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      this.hidden = true;
      return;
    } finally {
      this.#activeFetch = null;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const rawItems = /** @type {HTMLElement[]} */ (
      Array.from(doc.querySelectorAll('.resource-list__item[data-handle]'))
    );

    // Dedupe by data-handle. The Section Rendering API response contains
    // the section's desktop grid AND (when `carousel_on_mobile` is on) the
    // mobile carousel — each with its own copy of each product card. Without
    // this filter we'd render each product N times (where N = number of
    // containers rendered by the section).
    const seenHandles = new Set();
    const items = rawItems.filter((el) => {
      const h = el.dataset.handle ?? '';
      if (!h || seenHandles.has(h)) return false;
      seenHandles.add(h);
      return true;
    });

    if (items.length === 0) {
      this.hidden = true;
      return;
    }

    // Re-sort by localStorage insertion order.
    const order = new Map(entries.map((entry, i) => [entry.handle, i]));
    items.sort((a, b) => {
      const ai = order.get(a.dataset.handle ?? '') ?? Number.MAX_SAFE_INTEGER;
      const bi = order.get(b.dataset.handle ?? '') ?? Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });

    // Find all resource-list containers (desktop grid + optional mobile
    // carousel) inside our element and replace their children. The section
    // Liquid renders two copies when `carousel_on_mobile` is true; each
    // needs its own set of cloned items.
    const containers = this.querySelectorAll('.resource-list');
    if (containers.length === 0) {
      this.hidden = true;
      return;
    }

    containers.forEach((container, containerIndex) => {
      // Clone nodes per container (same Node can't be attached in two places).
      const nodes = items.map((node) => /** @type {HTMLElement} */ (node.cloneNode(true)));
      // Keep the first container's live nodes; others get deep clones.
      const targetNodes = containerIndex === 0 ? items : nodes;
      container.replaceChildren(...targetNodes);
    });
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
